import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import articles from '../data/blog/index.json';

export { articles };

export const categories = {
  en: { all: 'All topics', basics: 'The basics', family: 'Family & planning', health: 'Health & eligibility', coverage: 'Your coverage' },
  es: { all: 'Todos los temas', basics: 'Lo esencial', family: 'Familia y planificación', health: 'Salud y requisitos', coverage: 'Tu cobertura' },
};

export const articlePath = (article, language) => `/Blog/${language}/${article.slug}`;
export const normalizeSearch = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();

export function useBlogMetadata(title, description) {
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute('content');
    document.title = `${title} | Birimo Insurance`;
    if (meta) meta.setAttribute('content', description);
    return () => {
      document.title = previousTitle;
      if (meta) meta.setAttribute('content', previousDescription || '');
    };
  }, [title, description]);
}

export function LanguageSwitch({ language, pathFor }) {
  return (
    <nav aria-label={language === 'es' ? 'Idioma del contenido' : 'Content language'} className="inline-flex w-fit rounded-full border border-slate-200 bg-white p-1 text-sm font-semibold text-slate-600 shadow-sm">
      {['en', 'es'].map((code) => (
        <Link key={code} to={pathFor(code)} lang={code} hrefLang={code} aria-current={code === language ? 'page' : undefined}
          className={`rounded-full px-5 py-2.5 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navcolor ${code === language ? 'bg-navcolor text-white' : 'hover:bg-slate-100'}`}>
          {code === 'en' ? 'English' : 'Español'}
        </Link>
      ))}
    </nav>
  );
}

export function ArticleCard({ article, language = 'en', featured = false }) {
  const copy = article[language];
  return (
    <article lang={language} className={`group overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:border-navcolor/30 hover:shadow-lg ${featured ? 'md:grid md:grid-cols-2' : 'flex h-full flex-col'}`}>
      <Link to={articlePath(article, language)} tabIndex={-1} aria-hidden="true" className={`block overflow-hidden bg-[#f8e4c5] ${featured ? 'flex items-center' : ''}`}>
        <img src={article.image} alt="" width={article.imageWidth} height={article.imageHeight} loading={featured ? 'eager' : 'lazy'}
          className={`w-full object-cover transition duration-500 motion-safe:group-hover:scale-[1.03] ${featured ? 'h-auto' : 'aspect-[16/9]'}`} />
      </Link>
      <div className={`flex flex-1 flex-col ${featured ? 'p-7 md:p-10 lg:p-12' : 'p-6'}`}>
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-semibold">
          <span className="text-navcolor">{categories[language][article.category]}</span>
          <span className="flex items-center gap-1.5 text-slate-500"><ClockIcon className="h-3.5 w-3.5" aria-hidden="true" />{copy.readingMinutes} {language === 'es' ? 'min de lectura' : 'min read'}</span>
        </div>
        <h2 className={`font-serif font-bold leading-tight text-slate-900 ${featured ? 'text-3xl lg:text-4xl' : 'text-2xl'}`}>
          <Link to={articlePath(article, language)} className="rounded-sm transition hover:text-navcolor focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-navcolor">{copy.title}</Link>
        </h2>
        <p className="mt-4 text-base leading-7 text-slate-600">{copy.excerpt}</p>
        <Link to={articlePath(article, language)} aria-label={`${language === 'es' ? 'Leer' : 'Read'}: ${copy.title}`} className="mt-auto flex w-fit items-center gap-2 pt-6 text-sm font-bold text-navcolor underline-offset-4 hover:underline">
          {language === 'es' ? 'Leer artículo' : 'Read the article'}<ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}
